import api from '../../../shared/utils/api';
import type {
  EmailSignatureData,
  LoginRequest,
  LoginResponse,
  User,
} from '../../../shared/types';

interface LinkedInAuthUrlResponse {
  auth_url: string;
  state: string;
}

export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const { data } = await api.post<LoginResponse>('/auth/login', credentials);
    return data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },

  refresh: async (): Promise<{ accessToken: string }> => {
    const { data } = await api.post<{ accessToken: string }>('/auth/refresh', null, {
      __skipAuthRefresh: true,
    } as Parameters<typeof api.post>[2]);
    return data;
  },

  getMe: async (): Promise<User> => {
    const { data } = await api.get<User>('/auth/me');
    return data;
  },

  /**
   * Persist the user's email signature server-side (cross-device). Replaces
   * the previous localStorage-only store. Routes through the BFF to
   * janus-auth `PATCH /api/v1/users/{id}/email-signature`.
   */
  updateEmailSignature: async (
    userId: string | number,
    signature: EmailSignatureData | null,
  ): Promise<User> => {
    const { data } = await api.patch<User>(`/users/${userId}/email-signature`, {
      email_signature: signature,
    });
    return data;
  },

  validate: async (): Promise<void> => {
    await api.get('/auth/validate');
  },

  getLinkedInAuthUrl: async (redirectUri?: string): Promise<LinkedInAuthUrlResponse> => {
    const { data } = await api.get<LinkedInAuthUrlResponse>('/auth/linkedin/authorize', {
      params: redirectUri ? { redirect_uri: redirectUri } : undefined,
    });
    return data;
  },

  linkedInCallback: async (code: string, state: string, redirectUri?: string): Promise<LoginResponse> => {
    const { data } = await api.post<LoginResponse>('/auth/linkedin/callback', {
      code,
      state,
      redirect_uri: redirectUri,
    });
    return data;
  },

  forgotPassword: async (email: string): Promise<void> => {
    await api.post('/auth/forgot-password', { email });
  },

  verifyResetToken: async (token: string): Promise<void> => {
    await api.post('/auth/verify-reset-token', { token });
  },

  resetPassword: async (
    token: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<void> => {
    await api.post('/auth/reset-password', {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  },

  /** Microsoft SSO: ask janus-auth for the consent URL to redirect the browser to. */
  getMicrosoftAuthorizeUrl: async (): Promise<{ authUrl: string }> => {
    const { data } = await api.get<{ auth_url: string }>('/auth/microsoft/authorize', {
      __public: true,
      __skipRetry: true,
      __skipAuthRefresh: true,
      __skipErrorNotification: true,
    } as Parameters<typeof api.get>[1]);
    return { authUrl: data.auth_url };
  },
};
